// ═══ A2 검차 목록 `/main/s/requests` (신고 371 · 앱 EvalOrdersScreen(simple)) ═══
// 그룹 필터 pill(전체/진행 중/결정 대기/종료 + 카운트) + 카드 목록. ≤1023 은 카드 클릭이 **상세 화면으로 이동** —
// 목록과 상세를 한 화면에 합치지 않는다(결정표 1). 초기 그룹은 홈에서 넘어온 `?group=` 프리셋.
// ≥1024(§8 C1) 는 2-pane 워크벤치 — 같은 화면이 `/main/s/requests/:id` 도 렌더하고(routes.js RequestsOrDetail),
// 행 클릭은 URL 만 바꿔 우측 패널(requests/DetailBody)을 갈아끼운다. 검색창은 만들지 않는다(결정 8).
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { T, R, FONT } from '../../theme';
import { useT } from '../../../i18n/I18nProvider';
import { useIsMobile } from '../../../utils/useMobile';
import { useIsDesktop } from '../useDesktop';
import { listOrders } from '../api';
import { describeSimpleError } from '../errors';
import { groupOf, sortActive } from '../fmt';
import ErrorText from '../parts/ErrorText';
import LtrText from '../parts/LtrText';
import GroupChips from './requests/GroupChips';
import RequestCard from './requests/RequestCard';
import DetailBody from './requests/DetailBody';

var GROUPS = [
  { id: 'all', labelKey: 'simpleAll' },
  { id: 'inProgress', labelKey: 'simpleGroupInProgress' },
  { id: 'actionNeeded', labelKey: 'simpleGroupActionNeeded', tone: 'amber' },
  { id: 'finished', labelKey: 'simpleGroupFinished' }
];

var titleRow = { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 13 };
var h2 = { fontSize: FONT.title, fontWeight: 800, color: T.ink, letterSpacing: '-.3px' };
var countStyle = { fontSize: 12.5, color: T.muted };
var ctaBtn = { marginInlineStart: 'auto', height: 34, padding: '0 14px', borderRadius: R.md, border: '1px solid ' + T.navy,
  background: T.navy, color: T.card, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', flex: 'none' };
var fab = { position: 'fixed', insetInlineEnd: 16, bottom: 74, zIndex: 59, height: 46, padding: '0 18px',
  borderRadius: R.pill, border: 0, background: T.navy, color: T.card, fontSize: 13.5, fontWeight: 700,
  cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 8px 24px rgba(15,23,42,.24)' };
var card = { background: T.card, border: '1px solid ' + T.bd, borderRadius: R.card, overflow: 'clip' };
var empty = { padding: '56px 18px', textAlign: 'center' };
var emptyTitle = { fontSize: 14, fontWeight: 800, color: T.ink };
var emptySub = { fontSize: 12.5, color: T.muted, marginTop: 6, lineHeight: 1.6 };
var emptyCta = { marginTop: 14, height: 38, padding: '0 16px', borderRadius: R.md, border: '1px solid ' + T.navy,
  background: T.navy, color: T.card, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };
var loadingStyle = { padding: '40px 0', textAlign: 'center', fontSize: 12.5, color: T.muted };

// ── 데스크톱 좌 목록 패널(시안 C1 B `.lpanel`) — 패널 자체가 카드라 안에서 또 감싸지 않는다. ──
var lhStyle = { position: 'sticky', top: 0, zIndex: 1, display: 'flex', flexDirection: 'column', gap: 9,
  padding: '11px 14px', borderBottom: '1px solid ' + T.bd, background: T.head };
var lhRow = { display: 'flex', alignItems: 'center', gap: 9 };
var lhTitle = { fontSize: 15, fontWeight: 800, letterSpacing: '-.3px', color: T.navy };
var lhCta = Object.assign({}, ctaBtn, { height: 30, padding: '0 12px', fontSize: 12 });
var paneEmpty = { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  gap: 6, padding: '40px 18px', textAlign: 'center' };

function newestFirst(a, b) {
  var da = String((a && a.created_at) || ''), db = String((b && b.created_at) || '');
  if (da !== db) return da < db ? 1 : -1;
  return Number((b && b.id) || 0) - Number((a && a.id) || 0);
}

/// 표시 순서 — 선택 그룹 안에서는 최신순, '전체' 는 결정 대기 → 진행 중 → 종료.
export function visibleOrders(rows, group) {
  var list = Array.isArray(rows) ? rows : [];
  if (group && group !== 'all') {
    return list.filter(function (o) { return groupOf(o && o.status) === group; }).sort(newestFirst);
  }
  var finished = list.filter(function (o) { return groupOf(o && o.status) === 'finished'; }).sort(newestFirst);
  // sortActive 는 홈용으로 최근 취소(7일)를 세 번째 그룹에 얹는다 — 이 화면은 그룹 정의 그대로라
  // 종료 건은 아래 finished 자리에만 둔다(중복 행 방지).
  var active = sortActive(list).filter(function (o) { return groupOf(o && o.status) !== 'finished'; });
  return active.concat(finished);
}

function SimpleRequests(props) {
  var t = useT();
  var navigate = useNavigate();
  var isMobile = useIsMobile();
  var isDesktop = useIsDesktop();
  var selectedId = useParams().id;
  var sp = useSearchParams(); var params = sp[0], setParams = sp[1];
  var stR = useState([]); var rows = stR[0], setRows = stR[1];
  var stL = useState(true); var loading = stL[0], setLoading = stL[1];
  var stE = useState(''); var err = stE[0], setErr = stE[1];
  var preset = params.get('group');
  var stG = useState(preset && GROUPS.some(function (g) { return g.id === preset; }) ? preset : 'all');
  var group = stG[0], setGroup = stG[1];

  var load = useCallback(function () {
    setErr('');
    return listOrders({}).then(function (body) {
      setRows(Array.isArray(body && body.data) ? body.data : []);
      setLoading(false);
    }, function (e) {
      setErr(describeSimpleError(t, e));
      setLoading(false);
    });
  // setter 3종은 useState 가 보장하는 안정 참조 — 배열 구조분해라 린터가 못 알아본다.
  }, [t, setRows, setLoading, setErr]);

  useEffect(function () { load(); }, [load]);

  // 셸이 소켓 eval:updated/completed 를 재발행한다 — 목록은 이 이벤트만 듣는다.
  useEffect(function () {
    var on = function () { load(); };
    window.addEventListener('autodex:eval-updated', on);
    return function () { window.removeEventListener('autodex:eval-updated', on); };
  }, [load]);

  var items = GROUPS.map(function (g) {
    return {
      id: g.id, tone: g.tone, label: t(g.labelKey),
      count: g.id === 'all' ? rows.length : rows.filter(function (o) { return groupOf(o && o.status) === g.id; }).length
    };
  });
  var list = visibleOrders(rows, group);
  var goNew = function () { navigate('/main/s/request'); };

  // 데스크톱은 목록↔상세가 서로 다른 라우트 element 라 첫 행 클릭에서 화면이 다시 마운트된다 —
  // 칩 선택은 `?group=` 에 남겨 유지한다(모바일은 현행 그대로 state 만 바꾼다).
  var chooseGroup = function (g) {
    setGroup(g);
    if (isDesktop) setParams(g === 'all' ? {} : { group: g }, { replace: true });
  };

  if (isDesktop) {
    var qs = params.toString();
    var openOrder = function (id) { navigate('/main/s/requests/' + id + (qs ? '?' + qs : '')); };
    return (<div className="ss-requests ss-2col">
      <div className="ss-list-pane">
        <div style={lhStyle}>
          <div style={lhRow}>
            <h2 style={lhTitle}>{t('shellTabEval')}</h2>
            <LtrText style={countStyle}>{t('simpleB1TotalCount', { count: rows.length })}</LtrText>
            <button type="button" style={lhCta} onClick={goNew}>{t('simpleB1HeroCta')}</button>
          </div>
          <GroupChips items={items} value={group} onChange={chooseGroup} />
        </div>

        <ErrorText>{err}</ErrorText>

        {loading ? <div style={loadingStyle}>{t('commonLoading')}</div> : (list.length ? list.map(function (o) {
          return (<RequestCard key={o.id} order={o} variant="pane" selected={String(o.id) === String(selectedId)}
            onClick={function () { openOrder(o.id); }} />);
        }) : (<div style={empty}>
          <div style={emptyTitle}>{t('evalEmpty')}</div>
          <div style={emptySub}>{t('evalEmptyMessage')}</div>
          <button type="button" style={emptyCta} onClick={goNew}>{t('simpleB1HeroCta')}</button>
        </div>))}
      </div>

      {selectedId
        ? <DetailBody key={selectedId} id={selectedId} user={props.user} layout="pane" onChanged={load} />
        : (<div className="ss-detail-pane">
          <div style={paneEmpty}>
            <div style={emptyTitle}>{t('evalEmpty')}</div>
            <div style={emptySub}>{t('evalEmptyMessage')}</div>
          </div>
        </div>)}
    </div>);
  }

  return (<div className="ss-requests">
    <div style={titleRow}>
      <h2 style={h2}>{t('shellTabEval')}</h2>
      <LtrText style={countStyle}>{t('simpleB1TotalCount', { count: rows.length })}</LtrText>
      {isMobile ? null : <button type="button" style={ctaBtn} onClick={goNew}>{t('simpleB1HeroCta')}</button>}
    </div>

    <div style={{ marginBottom: 13 }}>
      <GroupChips items={items} value={group} onChange={setGroup} />
    </div>

    <ErrorText>{err}</ErrorText>

    {loading ? <div style={loadingStyle}>{t('commonLoading')}</div> : (<div style={card}>
      {list.length ? list.map(function (o) {
        return (<RequestCard key={o.id} order={o} wide={!isMobile}
          onClick={function () { navigate('/main/s/requests/' + o.id); }} />);
      }) : (<div style={empty}>
        <div style={emptyTitle}>{t('evalEmpty')}</div>
        <div style={emptySub}>{t('evalEmptyMessage')}</div>
        <button type="button" style={emptyCta} onClick={goNew}>{t('simpleB1HeroCta')}</button>
      </div>)}
    </div>)}

    {isMobile ? <button type="button" style={fab} onClick={goNew}>{t('simpleB1HeroCta')}</button> : null}
  </div>);
}

export default SimpleRequests;
